import { useCallback, useEffect, useMemo, useState } from 'react';
import { getContacts } from '../../../services/modules/contactService';
import { useChatContext } from '../../../contexts/useChatContext';
import {
  filterPickItems,
  toPickItemsFromContacts,
  toYouPickItem,
  type ChatContactPickItem,
} from '../lib/chatContactPicker';

export function useChatContactPicker(isOpen: boolean) {
  const { selectedConnection } = useChatContext();
  const [query, setQuery] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [rawContacts, setRawContacts] = useState<ChatContactPickItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const you = useMemo(() => toYouPickItem(selectedConnection), [selectedConnection]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setIsLoading(true);
    void (async () => {
      try {
        const rows = await getContacts();
        if (cancelled) return;
        setRawContacts(toPickItemsFromContacts(rows));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const contacts = useMemo(
    () => filterPickItems(rawContacts, query),
    [rawContacts, query],
  );

  const youVisible = useMemo(() => {
    if (!you) return null;
    return filterPickItems([you], query)[0] ?? null;
  }, [you, query]);

  const selectedItem = useMemo(() => {
    if (!selectedKey) return null;
    if (you && you.key === selectedKey) return you;
    return rawContacts.find((c) => c.key === selectedKey) ?? null;
  }, [selectedKey, you, rawContacts]);

  const selectKey = useCallback((key: string | null) => {
    setSelectedKey(key);
  }, []);

  const reset = useCallback(() => {
    setQuery('');
    setSelectedKey(null);
  }, []);

  return {
    query,
    setQuery,
    selectedKey,
    selectKey,
    you: youVisible,
    contacts,
    isLoading,
    selectedItem,
    reset,
  };
}
